import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { X, RotateCcw } from "lucide-react";

const AccessibilityWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [fontSize, setFontSize] = useState(100);
  const [highContrast, setHighContrast] = useState(false);
  const [highlightLinks, setHighlightLinks] = useState(false);
  const [bigCursor, setBigCursor] = useState(false);

  // Load settings from localStorage on mount
  useEffect(() => {
    const savedSettings = localStorage.getItem("accessibilitySettings");
    if (savedSettings) {
      const settings = JSON.parse(savedSettings);
      setFontSize(settings.fontSize || 100);
      setHighContrast(settings.highContrast || false);
      setHighlightLinks(settings.highlightLinks || false);
      setBigCursor(settings.bigCursor || false);
    }
  }, []);

  // Save settings to localStorage whenever they change
  useEffect(() => {
    const settings = { fontSize, highContrast, highlightLinks, bigCursor };
    localStorage.setItem("accessibilitySettings", JSON.stringify(settings));
    
    // Apply font size
    document.documentElement.style.fontSize = `${fontSize}%`;
    
    // Apply high contrast
    if (highContrast) {
      document.body.classList.add("high-contrast");
    } else {
      document.body.classList.remove("high-contrast");
    }
    
    // Apply highlight links
    if (highlightLinks) {
      document.body.classList.add("highlight-links");
    } else {
      document.body.classList.remove("highlight-links");
    }
    
    // Apply big cursor
    if (bigCursor) {
      document.body.classList.add("big-cursor");
    } else {
      document.body.classList.remove("big-cursor");
    }
  }, [fontSize, highContrast, highlightLinks, bigCursor]);

  const increaseFontSize = () => {
    setFontSize((prev) => Math.min(prev + 10, 150));
  };

  const decreaseFontSize = () => {
    setFontSize((prev) => Math.max(prev - 10, 80));
  };

  const resetSettings = () => {
    setFontSize(100);
    setHighContrast(false);
    setHighlightLinks(false);
    setBigCursor(false);
  };

  return (
    <>
      {/* Accessibility Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 left-6 z-50 w-14 h-14 bg-[#0F4C81] hover:bg-[#0a3d6a] text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110"
        aria-label="Open Accessibility Menu"
        data-testid="accessibility-widget-btn"
      >
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 24 24" 
          fill="currentColor" 
          className="w-7 h-7"
        >
          <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9H15V22H13V16H11V22H9V9H3V7H21V9Z"/>
        </svg>
      </button>

      {/* Accessibility Panel */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/30 z-50"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Panel */}
          <div 
            className="fixed bottom-6 left-6 z-50 w-80 bg-white rounded-2xl shadow-2xl overflow-hidden"
            role="dialog"
            aria-label="Accessibility Settings"
          >
            {/* Header */}
            <div className="bg-[#0F4C81] text-white px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  viewBox="0 0 24 24" 
                  fill="currentColor" 
                  className="w-6 h-6"
                >
                  <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9H15V22H13V16H11V22H9V9H3V7H21V9Z"/>
                </svg>
                <span className="font-semibold text-lg">Accessibility</span>
              </div>
              <button 
                onClick={() => setIsOpen(false)}
                className="hover:bg-white/20 p-1 rounded-full transition-colors"
                aria-label="Close accessibility menu"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-5">
              {/* Font Size */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Font Size
                </label>
                <div className="flex items-center justify-between bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={decreaseFontSize}
                    className="flex-1 py-2 px-4 text-lg font-bold text-gray-700 hover:bg-gray-200 rounded-md transition-colors"
                    aria-label="Decrease font size"
                  >
                    A-
                  </button>
                  <span className="flex-1 text-center font-semibold text-[#0F4C81]">
                    {fontSize}%
                  </span>
                  <button
                    onClick={increaseFontSize}
                    className="flex-1 py-2 px-4 text-lg font-bold text-gray-700 hover:bg-gray-200 rounded-md transition-colors"
                    aria-label="Increase font size"
                  >
                    A+
                  </button>
                </div>
              </div>

              {/* High Contrast */}
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">High Contrast</span>
                <button
                  onClick={() => setHighContrast(!highContrast)}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    highContrast ? "bg-[#0F4C81]" : "bg-gray-300"
                  }`}
                  role="switch"
                  aria-checked={highContrast}
                  aria-label="Toggle high contrast"
                >
                  <span
                    className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                      highContrast ? "translate-x-6" : "translate-x-0"
                    }`}
                  />
                </button>
              </div>

              {/* Highlight Links */}
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Highlight Links</span>
                <button
                  onClick={() => setHighlightLinks(!highlightLinks)}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    highlightLinks ? "bg-[#0F4C81]" : "bg-gray-300"
                  }`}
                  role="switch"
                  aria-checked={highlightLinks}
                  aria-label="Toggle highlight links"
                >
                  <span
                    className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                      highlightLinks ? "translate-x-6" : "translate-x-0"
                    }`}
                  />
                </button>
              </div>

              {/* Big Cursor */}
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Big Cursor</span>
                <button
                  onClick={() => setBigCursor(!bigCursor)}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    bigCursor ? "bg-[#0F4C81]" : "bg-gray-300"
                  }`}
                  role="switch"
                  aria-checked={bigCursor}
                  aria-label="Toggle big cursor"
                >
                  <span
                    className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                      bigCursor ? "translate-x-6" : "translate-x-0"
                    }`}
                  />
                </button>
              </div>

              {/* Reset Button */}
              <button
                onClick={resetSettings}
                className="w-full py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg flex items-center justify-center gap-2 transition-colors"
              >
                <RotateCcw className="w-4 h-4" />
                Reset Settings
              </button>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t">
              <Link 
                to="/website-accessibility"
                onClick={() => setIsOpen(false)}
                className="text-sm text-[#0F4C81] hover:underline flex items-center justify-center"
              >
                Accessibility Statement
              </Link>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default AccessibilityWidget;
